import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createApiClient } from '@adieuu/shared';
import { useAppConfig } from '../../config/PlatformContext';
import { useCall } from '../../hooks/useCall';
import { CallSessionError, useCallSession } from '../../hooks/useCallSession';
import { useConversations } from '../../hooks/useConversations';
import { forceEndCall as apiForceEndCall } from '../../services/callService';
import { useToast } from '../Toast';
import { CallDeviceSetupModal, type CallDeviceSelection } from './CallDeviceSetupModal';
import { CallOverlayShell } from './CallOverlayShell';
import { CallTroubleshootModal } from './CallTroubleshootModal';

/**
 * AppCallOverlay
 *
 * Renders the LiveKit VideoConference when a conversation call is active.
 * - Always mounted globally so the audio connection persists across navigation.
 * - Only shows the video UI when the user is viewing the conversation
 *   the call belongs to (via data-call-visible attribute for CSS).
 * - Positioned within the conversation body area (top half), not full viewport.
 */
export function AppCallOverlay() {
  const { t } = useTranslation();
  const { apiBaseUrl } = useAppConfig();
  const { activeConversationId, conversations } = useConversations();
  const { activeCall, refetch: refetchActiveCall } = useCall(activeConversationId);
  const toast = useToast();
  const [troubleshootOpen, setTroubleshootOpen] = useState(false);

  const apiClient = useMemo(
    () => createApiClient({ baseUrl: apiBaseUrl }).client,
    [apiBaseUrl],
  );

  const {
    activeSession,
    phase,
    pendingCallType,
    pendingIsJoin,
    confirmDeviceSetup,
    cancelDeviceSetup,
    leaveCall,
    livekitUrl,
    livekitToken,
    streamQualityCaps,
    callE2EEKey,
    e2eeSupported,
  } = useCallSession();

  // Warm the LiveKit chunk as soon as a call is being set up so it's ready by
  // the time the room mounts (import() is deduped, so this is cheap).
  useEffect(() => {
    if (phase === 'device-setup' || activeSession) {
      void import('./CallRoom');
    }
  }, [phase, activeSession]);

  const handleConfirmDevices = useCallback(
    async (devices: CallDeviceSelection) => {
      try {
        await confirmDeviceSetup(devices);
      } catch (err) {
        if (err instanceof CallSessionError && err.code === 'ALREADY_IN_CALL') {
          toast.toast({
            title: t('call.alreadyJoinedCall'),
            variant: 'error',
            action: {
              label: t('call.troubleshootLink'),
              onClick: () => setTroubleshootOpen(true),
            },
          });
          return;
        }
        const message = err instanceof Error ? err.message : t('call.callStartFailed');
        toast.error(message);
      }
    },
    [confirmDeviceSetup, toast, t],
  );

  const handleConnected = useCallback(() => {
    console.info(
      '[AppCallOverlay] LiveKit room connected:',
      { e2eeActive: !!callE2EEKey, e2eeSupported },
    );
  }, [callE2EEKey, e2eeSupported]);

  const handleDisconnected = useCallback(() => {
    void leaveCall();
  }, [leaveCall]);

  const handleForceEndCall = useCallback(async () => {
    const conversationId = activeSession?.conversationId ?? activeConversationId;
    const callId = activeSession?.call.id ?? activeCall?.id;
    if (!conversationId || !callId) return false;

    try {
      const result = await apiForceEndCall(apiClient, conversationId, callId);
      if (result.success) {
        toast.success(t('call.forceEndSuccess'));
        await leaveCall();
        await refetchActiveCall();
        return true;
      }
      toast.error(t('call.forceEndFailed'));
      return false;
    } catch (err) {
      console.warn('[AppCallOverlay] force end call failed', err);
      toast.error(t('call.forceEndFailed'));
      return false;
    }
  }, [
    activeSession?.conversationId,
    activeSession?.call.id,
    activeConversationId,
    activeCall?.id,
    apiClient,
    toast,
    t,
    leaveCall,
    refetchActiveCall,
  ]);

  const isViewingCallConversation =
    activeSession !== null && activeConversationId === activeSession.conversationId;

  const isCallDm = useMemo(() => {
    if (!activeSession) return false;
    const conversation = conversations.find((c) => c.id === activeSession.conversationId);
    return conversation?.type === 'dm';
  }, [activeSession, conversations]);

  return (
    <>
      <CallDeviceSetupModal
        open={phase === 'device-setup' && pendingCallType !== null}
        isJoin={pendingIsJoin}
        onConfirm={handleConfirmDevices}
        onCancel={cancelDeviceSetup}
      />

      {activeSession && livekitUrl && livekitToken && (
        <CallOverlayShell
          variant="conversation"
          visible={isViewingCallConversation}
          phase={phase}
          serverUrl={livekitUrl}
          token={livekitToken}
          callE2EEKey={callE2EEKey}
          e2eeSupported={e2eeSupported}
          e2eeExpected={true}
          streamQualityCaps={streamQualityCaps}
          isDm={isCallDm}
          onConnected={handleConnected}
          onDisconnected={handleDisconnected}
          onTroubleshoot={() => setTroubleshootOpen(true)}
        />
      )}

      <CallTroubleshootModal
        open={troubleshootOpen}
        onOpenChange={setTroubleshootOpen}
        onForceEnd={handleForceEndCall}
      />
    </>
  );
}
